import { useMemo, useState } from 'react';

import { useTranslate } from '@i18n';
import { Button, typography, ToggleButton, colors, CaretRightIcon } from '@ui';

import * as css from './CustomModuleEditor.css';
import { useGraphParameterMap } from './useGraphParameterMap';

type ParameterSelector = Partial<{
    position: string;
    axis: string;
}>;

type ParameterSelecetorProps = {
    name: string;
    graphId: string;
    onAdd: (parameterID: string) => void;
};

const comparePositions = (a: string, b: string) => {
    return Number(a.substring(1)) - Number(b.substring(1));
};

export const GraphParameterSelector = ({ name, graphId, onAdd }: ParameterSelecetorProps) => {
    const graphParameterMap = useGraphParameterMap(graphId);

    const [selectorState, setSelectorState] = useState<ParameterSelector>({});

    const handleAdd = () => {
        if (selectorState.position && selectorState.axis) {
            const parameterNode = graphParameterMap[selectorState.position][selectorState.axis];
            onAdd(parameterNode.id);
            setSelectorState({});
        }
    };

    const positions = useMemo(() => Object.keys(graphParameterMap).sort(comparePositions), [graphParameterMap]);

    const t = useTranslate();

    return (
        <div className={css.parameterSelectorLayout}>
            <div className={css.parameterSelector}>
                <div className={css.parameterSelectorTitle}>
                    <h3 className={typography({ variant: 'h3' })}>{name}</h3>
                </div>
                <div className={css.parameterSelectorButtons}>
                    {positions.map((position) => {
                        const isActive = selectorState.position === position;
                        const parametersByAxis = graphParameterMap[position];
                        if (!parametersByAxis) {
                            return null;
                        }
                        const axes = Object.keys(parametersByAxis).sort((a, b) => a.localeCompare(b));
                        const defaultAxis = axes.length > 1 ? null : axes[0];

                        const isExpanded = isActive && !defaultAxis;

                        return (
                            <div key={position} className={css.toggleGroup({ isActive })}>
                                <ToggleButton
                                    key="position-toggle"
                                    variant="darker"
                                    isActive={isActive}
                                    onChange={(setIsActive) => {
                                        setSelectorState(
                                            setIsActive
                                                ? {
                                                      position,
                                                      ...(defaultAxis && { axis: defaultAxis }),
                                                  }
                                                : {},
                                        );
                                    }}
                                >
                                    {position}
                                </ToggleButton>
                                {isExpanded && <CaretRightIcon weight="regular" color={colors.bluegray[600]} />}
                                {isExpanded &&
                                    axes.map((axis) => (
                                        <ToggleButton
                                            key={axis}
                                            variant="darker"
                                            isActive={selectorState.axis === axis}
                                            onChange={(isActiveAxis) => {
                                                setSelectorState((prev) => ({
                                                    ...prev,
                                                    axis: isActiveAxis ? axis : undefined,
                                                }));
                                            }}
                                        >
                                            {axis}
                                        </ToggleButton>
                                    ))}
                            </div>
                        );
                    })}
                </div>
            </div>

            <Button
                variant="secondary"
                size="small"
                isDisabled={!selectorState.axis || !selectorState.position}
                onClick={handleAdd}
            >
                {t('layout_editor.custom_module.add_button')}
            </Button>
        </div>
    );
};
